import type { CaseManifestV1, LayerASpec } from "@cold-case/shared/case-manifest";
import { BUILDING_FOOTPRINT_TILES, TILE_SIZE } from "@cold-case/shared/constants";
import { isNpcIndoor } from "@cold-case/shared/npc-placement";
import { tileInWalkStrip } from "@cold-case/shared/layer-walk";

import {
  poiKindSuffix,
  resolveBuildingDisplayName,
  zoneDisplayName,
} from "../lib/building-display.js";
import { npcDisplayName } from "../lib/manifest-display.js";

export type MapPoiKind = "npc" | "building" | "transit";

export type MapPoi = {
  kind: MapPoiKind;
  instanceId: string;
  label: string;
  suffix: string;
  zone: "downtown" | "maple_heights";
  tile_x: number;
  tile_y: number;
  wx: number;
  wy: number;
  discovered: boolean;
  decor?: boolean;
  /** NPC is inside a building — map pin is on the building, not the street spine. */
  indoors?: boolean;
  indoorBuildingId?: string;
  isCrimeScene?: boolean;
};

export type MapPoiPartition = {
  buildings: MapPoi[];
  npcs: MapPoi[];
  transit: MapPoi[];
};

export const UNDISCOVERED_POI_LABEL = "???";

export function mapPoiDisplayLabel(poi: MapPoi): string {
  if (poi.kind === "transit") return poi.label;
  if (poi.isCrimeScene) return poi.label;
  if (poi.discovered) return poi.label;
  return UNDISCOVERED_POI_LABEL;
}

export function sortMapPoisByLabel(pois: MapPoi[]): MapPoi[] {
  return [...pois].sort((a, b) => {
    const byLabel = mapPoiDisplayLabel(a).localeCompare(mapPoiDisplayLabel(b), undefined, {
      sensitivity: "base",
    });
    if (byLabel !== 0) return byLabel;
    return a.instanceId.localeCompare(b.instanceId);
  });
}

export function partitionMapPois(pois: MapPoi[]): MapPoiPartition {
  const buildings: MapPoi[] = [];
  const npcs: MapPoi[] = [];
  const transit: MapPoi[] = [];
  for (const p of pois) {
    if (p.kind === "building") buildings.push(p);
    else if (p.kind === "npc") npcs.push(p);
    else if (p.kind === "transit") transit.push(p);
  }
  return {
    buildings: sortMapPoisByLabel(buildings),
    npcs: sortMapPoisByLabel(npcs),
    transit: sortMapPoisByLabel(transit),
  };
}

export type BuildingMapCenter = {
  wx: number;
  wy: number;
  zone: "downtown" | "maple_heights";
  tile_x: number;
  tile_y: number;
};

/** Footprint center for a building placement (minimap blips, indoor players). */
export function buildingMapCenter(
  layerA: LayerASpec,
  buildingId: string
): BuildingMapCenter | null {
  const p = (layerA.building_placements ?? []).find((bp) => bp.id === buildingId);
  if (!p) return null;
  const centerTx = p.tile_x + BUILDING_FOOTPRINT_TILES / 2;
  const centerTy = p.tile_y + BUILDING_FOOTPRINT_TILES / 2;
  return {
    zone: p.zone,
    tile_x: centerTx,
    tile_y: centerTy,
    wx: centerTx * TILE_SIZE + TILE_SIZE / 2,
    wy: centerTy * TILE_SIZE + TILE_SIZE / 2,
  };
}

export type MinimapCrop = {
  tile_x0: number;
  tile_y0: number;
  tile_x1: number;
  tile_y1: number;
};

export type MinimapPayload = {
  viewZone: "downtown" | "maple_heights";
  crop: MinimapCrop;
  cols: number;
  rows: number;
  walkable: boolean[];
  pois: MapPoi[];
};

const MAX_DIM = 72;

function stripForZone(layerA: LayerASpec, zone: "downtown" | "maple_heights") {
  return zone === "downtown" ? layerA.downtown_walk : layerA.suburb_walk;
}

function cropBounds(strip: LayerASpec["downtown_walk"], margin = 3): MinimapCrop {
  return {
    tile_x0: Math.max(0, strip.tile_x0 - margin),
    tile_y0: Math.max(0, strip.tile_y0 - margin),
    tile_x1: strip.tile_x1 + margin,
    tile_y1: strip.tile_y1 + margin,
  };
}

function tileToCanvas(
  tx: number,
  ty: number,
  crop: MinimapCrop,
  canvasW: number,
  canvasH: number
): { cx: number; cy: number } {
  const cropW = Math.max(1, crop.tile_x1 - crop.tile_x0 + 1);
  const cropH = Math.max(1, crop.tile_y1 - crop.tile_y0 + 1);
  return {
    cx: ((tx + 0.5 - crop.tile_x0) / cropW) * canvasW,
    cy: ((ty + 0.5 - crop.tile_y0) / cropH) * canvasH,
  };
}

export function buildMapPoiList(
  manifest: CaseManifestV1,
  layerA: LayerASpec,
  journal: Record<string, unknown>
): MapPoi[] {
  const discoveredNpc = new Set((journal.discovered_npcs as string[]) ?? []);
  const discoveredB = new Set([
    ...((journal.discovered_building_ids as string[]) ?? []),
    ...((journal.visited_buildings as string[]) ?? []),
  ]);

  const pois: MapPoi[] = [];
  const buildingById = new Map((layerA.building_placements ?? []).map((bp) => [bp.id, bp]));
  for (const p of layerA.npc_placements ?? []) {
    const n = manifest.npcs.find((x) => x.instance_id === p.id);
    const zl = zoneDisplayName(manifest, p.zone);
    const label = npcDisplayName(manifest, p.id);
    const indoorBuildingId = n && isNpcIndoor(n) ? n.position_near_building!.trim() : "";
    const indoorPlacement = indoorBuildingId
      ? buildingById.get(indoorBuildingId)
      : undefined;
    if (indoorPlacement && indoorPlacement.zone === p.zone) {
      const centerTx = indoorPlacement.tile_x + BUILDING_FOOTPRINT_TILES / 2;
      const centerTy = indoorPlacement.tile_y + BUILDING_FOOTPRINT_TILES / 2;
      pois.push({
        kind: "npc",
        instanceId: p.id,
        label,
        suffix: poiKindSuffix("npc", zl, { indoors: true }),
        zone: p.zone,
        tile_x: centerTx,
        tile_y: centerTy,
        wx: centerTx * TILE_SIZE + TILE_SIZE / 2,
        wy: centerTy * TILE_SIZE + TILE_SIZE / 2,
        discovered: discoveredNpc.has(p.id),
        indoors: true,
        indoorBuildingId,
      });
      continue;
    }
    pois.push({
      kind: "npc",
      instanceId: p.id,
      label,
      suffix: poiKindSuffix("npc", zl),
      zone: p.zone,
      tile_x: p.tile_x,
      tile_y: p.tile_y,
      wx: p.tile_x * TILE_SIZE + TILE_SIZE / 2,
      wy: p.tile_y * TILE_SIZE + TILE_SIZE / 2,
      discovered: discoveredNpc.has(p.id),
    });
  }
  for (const p of layerA.building_placements ?? []) {
    const decor = p.decor === true;
    if (decor) continue;
    const b = manifest.buildings.find((x) => x.building_id === p.id);
    const zl = zoneDisplayName(manifest, p.zone);
    const label = resolveBuildingDisplayName(manifest, p.id, { decor });
    const centerTx = p.tile_x + BUILDING_FOOTPRINT_TILES / 2;
    const centerTy = p.tile_y + BUILDING_FOOTPRINT_TILES / 2;
    pois.push({
      kind: "building",
      instanceId: p.id,
      label,
      suffix: poiKindSuffix("building", zl),
      zone: p.zone,
      tile_x: centerTx,
      tile_y: centerTy,
      wx: centerTx * TILE_SIZE + TILE_SIZE / 2,
      wy: centerTy * TILE_SIZE + TILE_SIZE / 2,
      discovered: discoveredB.has(p.id),
      decor,
      isCrimeScene: b?.is_crime_scene === true,
    });
  }
  if (layerA.transit_downtown) {
    const zl = zoneDisplayName(manifest, "downtown");
    pois.push({
      kind: "transit",
      instanceId: "__transit_downtown__",
      label: "To Maple Heights",
      suffix: poiKindSuffix("transit", zl),
      zone: "downtown",
      tile_x: layerA.transit_downtown.tile_x,
      tile_y: layerA.transit_downtown.tile_y,
      wx: layerA.transit_downtown.tile_x * TILE_SIZE + TILE_SIZE / 2,
      wy: layerA.transit_downtown.tile_y * TILE_SIZE + TILE_SIZE / 2,
      discovered: true,
    });
  }
  if (layerA.transit_maple_heights) {
    const zl = zoneDisplayName(manifest, "maple_heights");
    pois.push({
      kind: "transit",
      instanceId: "__transit_maple_heights__",
      label: "To Downtown",
      suffix: poiKindSuffix("transit", zl),
      zone: "maple_heights",
      tile_x: layerA.transit_maple_heights.tile_x,
      tile_y: layerA.transit_maple_heights.tile_y,
      wx: layerA.transit_maple_heights.tile_x * TILE_SIZE + TILE_SIZE / 2,
      wy: layerA.transit_maple_heights.tile_y * TILE_SIZE + TILE_SIZE / 2,
      discovered: true,
    });
  }
  return pois;
}

export function buildMinimapPayload(
  manifest: CaseManifestV1,
  layerA: LayerASpec,
  journal: Record<string, unknown>,
  viewZone: "downtown" | "maple_heights"
): MinimapPayload {
  const strip = stripForZone(layerA, viewZone);
  const crop = cropBounds(strip);
  const cropW = crop.tile_x1 - crop.tile_x0 + 1;
  const cropH = crop.tile_y1 - crop.tile_y0 + 1;
  const strideC = Math.max(1, Math.ceil(cropW / MAX_DIM));
  const strideR = Math.max(1, Math.ceil(cropH / MAX_DIM));
  const cols = Math.max(1, Math.ceil(cropW / strideC));
  const rows = Math.max(1, Math.ceil(cropH / strideR));
  const walkable: boolean[] = [];
  for (let or = 0; or < rows; or++) {
    for (let oc = 0; oc < cols; oc++) {
      let any = false;
      for (let dr = 0; dr < strideR; dr++) {
        for (let dc = 0; dc < strideC; dc++) {
          const ty = crop.tile_y0 + or * strideR + dr;
          const tx = crop.tile_x0 + oc * strideC + dc;
          if (ty > crop.tile_y1) continue;
          if (tileInWalkStrip(strip, tx, ty)) any = true;
        }
      }
      walkable.push(any);
    }
  }

  const allPois = buildMapPoiList(manifest, layerA, journal);
  const pois = allPois.filter((p) => p.zone === viewZone);

  return { viewZone, crop, cols, rows, walkable, pois };
}

export type MinimapPlayerBlip = {
  kind: "self" | "peer";
  wx: number;
  wy: number;
  indoors?: boolean;
  caption?: string;
};

const MINIMAP_POI_RADIUS = 5;
const MINIMAP_PEER_RADIUS = 5;
const MINIMAP_SELF_RADIUS = 8;

function worldToMinimapCanvas(
  wx: number,
  wy: number,
  crop: MinimapCrop,
  canvasW: number,
  canvasH: number
): { cx: number; cy: number } {
  const tx = Math.floor(wx / TILE_SIZE);
  const ty = Math.floor(wy / TILE_SIZE);
  return tileToCanvas(tx, ty, crop, canvasW, canvasH);
}

const INDOOR_BLIP_CAPTION = "inside bldg";

function pushPlayerBlip(
  blips: MinimapPlayerBlip[],
  kind: "self" | "peer",
  viewZone: "downtown" | "maple_heights",
  zone: "downtown" | "maple_heights",
  wx: number,
  wy: number,
  interiorBuildingId: string | null | undefined,
  resolveIndoorCenter?: (buildingId: string) => BuildingMapCenter | null
): void {
  if (interiorBuildingId && resolveIndoorCenter) {
    const center = resolveIndoorCenter(interiorBuildingId);
    if (!center || center.zone !== viewZone) return;
    blips.push({
      kind,
      wx: center.wx,
      wy: center.wy,
      indoors: true,
      caption: INDOOR_BLIP_CAPTION,
    });
    return;
  }
  if (zone !== viewZone) return;
  if (!Number.isFinite(wx) || !Number.isFinite(wy)) return;
  blips.push({ kind, wx, wy });
}

/** Live player positions for the journal minimap (current view zone). */
export function collectMinimapPlayerBlips(opts: {
  viewZone: "downtown" | "maple_heights";
  self?: {
    zone: "downtown" | "maple_heights";
    wx: number;
    wy: number;
    interiorBuildingId?: string | null;
  };
  peers?: Array<{
    zone: "downtown" | "maple_heights";
    wx: number;
    wy: number;
    interiorBuildingId: string | null;
  }>;
  resolveIndoorCenter?: (buildingId: string) => BuildingMapCenter | null;
}): MinimapPlayerBlip[] {
  const blips: MinimapPlayerBlip[] = [];
  if (opts.self) {
    pushPlayerBlip(
      blips,
      "self",
      opts.viewZone,
      opts.self.zone,
      opts.self.wx,
      opts.self.wy,
      opts.self.interiorBuildingId,
      opts.resolveIndoorCenter
    );
  }
  for (const peer of opts.peers ?? []) {
    pushPlayerBlip(
      blips,
      "peer",
      opts.viewZone,
      peer.zone,
      peer.wx,
      peer.wy,
      peer.interiorBuildingId,
      opts.resolveIndoorCenter
    );
  }
  return blips;
}

export function paintMinimapCanvas(
  canvas: HTMLCanvasElement,
  payload: MinimapPayload,
  selectedId: string | null,
  players: MinimapPlayerBlip[] = []
): void {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const w = canvas.width;
  const h = canvas.height;
  const { cols, rows, walkable, crop } = payload;
  const cw = w / cols;
  const ch = h / rows;
  ctx.fillStyle = "#0f172a";
  ctx.fillRect(0, 0, w, h);
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const walk = walkable[r * cols + c];
      ctx.fillStyle = walk ? "#1e293b" : "#0a0e14";
      ctx.fillRect(c * cw, r * ch, cw + 1, ch + 1);
    }
  }
  for (const p of payload.pois) {
    const { cx, cy } = tileToCanvas(p.tile_x, p.tile_y, crop, w, h);
    const radius =
      p.instanceId === selectedId ? MINIMAP_SELF_RADIUS - 1 : MINIMAP_POI_RADIUS;
    const color =
      p.kind === "npc" && p.indoors
        ? "#f97316"
        : p.kind === "npc"
          ? "#ef4444"
          : p.kind === "building" && p.isCrimeScene
            ? "#b45309"
            : p.kind === "building"
              ? "#eab308"
              : "#14b8a6";
    ctx.globalAlpha = p.discovered || p.kind === "transit" ? 1 : 0.35;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fill();
    if (p.kind === "npc" && p.indoors) {
      const box = radius + 2.5;
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 1.5;
      ctx.strokeRect(cx - box, cy - box, box * 2, box * 2);
    }
    if (p.instanceId === selectedId) {
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(cx, cy, radius + 1.5, 0, Math.PI * 2);
      ctx.stroke();
    }
  }
  ctx.globalAlpha = 1;

  const drawBlipCaption = (cx: number, cy: number, caption: string): void => {
    ctx.font = "9px Inter, system-ui, sans-serif";
    ctx.fillStyle = "#e2e8f0";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(caption, cx + 7, cy);
  };

  const drawPlayerBlip = (
    pl: MinimapPlayerBlip,
    fill: string,
    radius: number,
    stroke?: string
  ): void => {
    const { cx, cy } = worldToMinimapCanvas(pl.wx, pl.wy, crop, w, h);
    ctx.fillStyle = fill;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fill();
    if (stroke) {
      ctx.strokeStyle = stroke;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.stroke();
    }
    if (pl.indoors) {
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(cx, cy, radius + 2, 0, Math.PI * 2);
      ctx.stroke();
    }
    if (pl.caption) drawBlipCaption(cx, cy, pl.caption);
  };

  for (const pl of players) {
    if (pl.kind === "peer") {
      drawPlayerBlip(pl, "#a855f7", MINIMAP_PEER_RADIUS);
    }
  }
  for (const pl of players) {
    if (pl.kind !== "self") continue;
    drawPlayerBlip(pl, "#3b82f6", MINIMAP_SELF_RADIUS, "#dbeafe");
  }
}

/** Pick nearest POI at canvas pixel (for click handling). */
export function findNearestMapPoiAtCanvas(
  payload: MinimapPayload,
  canvasWidth: number,
  canvasHeight: number,
  px: number,
  py: number,
  maxDistPx = 32
): MapPoi | null {
  if (!Number.isFinite(px) || !Number.isFinite(py)) return null;
  const maxD = maxDistPx * maxDistPx;
  let best: MapPoi | null = null;
  let bestD = maxD;
  for (const p of payload.pois) {
    const { cx, cy } = tileToCanvas(p.tile_x, p.tile_y, payload.crop, canvasWidth, canvasHeight);
    const d = (cx - px) ** 2 + (cy - py) ** 2;
    if (d < bestD) {
      bestD = d;
      best = p;
    }
  }
  return best;
}

export type MapWaypointTarget = {
  wx: number;
  wy: number;
  targetZone?: "downtown" | "maple_heights";
  viaTransit?: boolean;
  label?: string;
};

/** Resolve world waypoint from a map POI and the player's current zone. */
export function resolveMapWaypoint(
  poi: MapPoi,
  playerZone: "downtown" | "maple_heights",
  layerA: LayerASpec
): MapWaypointTarget {
  if (poi.zone === playerZone) {
    return { wx: poi.wx, wy: poi.wy, targetZone: poi.zone, label: poi.label };
  }
  const transit =
    playerZone === "downtown" ? layerA.transit_downtown : layerA.transit_maple_heights;
  if (
    !transit ||
    !Number.isFinite(transit.tile_x) ||
    !Number.isFinite(transit.tile_y)
  ) {
    return { wx: poi.wx, wy: poi.wy, targetZone: poi.zone, viaTransit: true, label: poi.label };
  }
  return {
    wx: transit.tile_x * TILE_SIZE + TILE_SIZE / 2,
    wy: transit.tile_y * TILE_SIZE + TILE_SIZE / 2,
    targetZone: poi.zone,
    viaTransit: true,
    label: poi.label,
  };
}

export function isValidWorldWaypoint(p: { wx: number; wy: number } | null): boolean {
  if (!p) return false;
  return Number.isFinite(p.wx) && Number.isFinite(p.wy);
}
